import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCardDraft } from "../common/useCardDraft";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { VAS_BANDS } from "../vasScale";
import { Stepper, YesNo } from "../controls";
import { CC_V2_REFRESH } from "./events";
import ChiefComplaintsService from "./ChiefComplaintsService";
import IcdAutocomplete from "./IcdAutocomplete";
import { latestSinceWhen } from "./episodeDates";
import {
  durationToSinceWhen,
  formatShortDate,
  sinceWhenToDuration,
  toIsoDateOnly,
  toIsoUtc,
} from "./dateHelpers";
import { CalendarIcon, ChevronUpIcon } from "../labInvestigations/labIcons";

/**
 * ONE complaint sheet — add or update — owning all of its own field state.
 *
 * Two write paths, switched by the presence of `onSave`:
 *
 *   Standalone (default, no `onSave`) — this is the Chief Complaints tab's
 *   own editor. Save posts directly to api-cc-v2-service via
 *   ChiefComplaintsService, reads a fresh version for optimistic-lock,
 *   emits CC_V2_REFRESH so the sidebar table refetches. The sheet-stack
 *   coordinates concurrent saves via `blockedBySibling` /
 *   `onSavingChange`.
 *
 *   Transcript mode (caller passes `onSave`) — this is the AI transcript
 *   modal's editor for chief-complaint cards (AI proposal, manual add,
 *   status update). Save hands the CC v2 body shape to `onSave` and the
 *   caller stashes it in the transcript's captured buffer; nothing lands
 *   in CC v2 until the review flow's Save-and-next fan-out. Optional
 *   props `initialPayload` (seed from an AI captured item), `themeClass`
 *   (mode-specific accent),
 *   and `menuClassName` (VAS Select z-index bump inside the modal
 *   overlay) enable the transcript UX without touching the standalone.
 *
 * Modes:
 *   add    "Card · Typhoid fever" (2961:3685) — complaint (ICD fuzzy search) ·
 *          Status VAS dropdown beside the REQUIRED "Consulted doctor?" segment ·
 *          duration steppers with the since-when auto-calc · description
 *          (optional, max 250 words) · Cancel / Save complaint.
 *   update "Update status" (2961:4126) — VAS · Consulted doctor? · Status
 *          update date · Description · Cancel / Update.
 *
 * Selecting VAS=0 (band `absent`) auto-resolves the complaint server-side —
 * that IS the resolve action.
 */

const MAX_DESC_WORDS = 250;

// Word counter for the "N / 250 words" hint. Same tokeniser the Dx form
// uses so the two sections read consistently.
const countWords = (s) => {
  const trimmed = (s || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

// Trim anything past the 250th word — the board caps the field ("Max 250
// words"), and trimming on change keeps the counter honest while typing.
const capWords = (text) => {
  const words = text.split(/(\s+)/);
  let count = 0;
  let out = "";
  for (const w of words) {
    if (!/\s/.test(w) && w.length > 0) {
      count += 1;
      if (count > MAX_DESC_WORDS) break;
    }
    out += w;
  }
  return count > MAX_DESC_WORDS ? out : text;
};

const initialBand = () => VAS_BANDS.find((b) => b.key === "mild") || VAS_BANDS[0];

const todayISO = () => new Date().toISOString().slice(0, 10);

// The band a stored score sits in. Mirrors the dialog's seed rule exactly: a
// missing/NaN vas matches no range and falls back to VAS_BANDS[0] (Resolved).
const bandForScore = (vas) =>
  VAS_BANDS.find((b) => b.min <= vas && vas <= b.max) || VAS_BANDS[0];

// Map an AI-emitted intensity phrase ("mild", "moderate", "severe") into a
// VAS band key. STRICT match only — if the phrase doesn't line up with
// one of the VAS_BANDS keys/labels, return "" so the dropdown renders
// empty and the reviewer picks manually. Silent-fallback to Mild
// (previous behaviour) was hiding LLM misclassifications behind a plausible
// default. Only used in transcript-mode seeding.
const bandKeyFromIntensity = (intensity) => {
  const s = String(intensity || "").trim().toLowerCase();
  if (!s) return "";
  const match = VAS_BANDS.find(
    (b) => b.key === s || b.label.toLowerCase().startsWith(s),
  );
  return match ? match.key : "";
};

// The dialog seeded its date box off Current.sinceWhen, falling back to today
// when the record carried nothing parseable.
const dateInputFrom = (sinceWhen) => {
  const d = sinceWhen ? new Date(sinceWhen) : new Date();
  return (isNaN(d.getTime()) ? new Date() : d).toISOString().slice(0, 10);
};

// Duration steppers seed from initialPayload.since_date (YYYY-MM-DD) when
// the transcript pre-fills the card. Without a seed we start EMPTY
// (null/null/null → "-" in each cell) — the "From today" toggle is what
// the reviewer flips to accept today. This replaces the old 0/0/0 default,
// which visually asserted "since today" before the user had chosen anything.
const durationFromPayloadSince = (sinceDate) => {
  if (!sinceDate) return { years: null, months: null, days: null };
  return sinceWhenToDuration(sinceDate);
};

// Consulted-doctor seed — order of preference:
//   1. Update mode with editing.current.consultedDoctor set (CC v2 record
//      or synthesised from a staged captured payload).
//   2. initialPayload.consulted_doctor when it's an explicit boolean
//      (AI captured item, or re-edit from the review flow's click-to-edit).
//   3. Update mode fallback → "no" (record was missing the field; Save
//      still needs a valid answer).
//   4. Fresh add/manual → null (user must pick — YesNo unset).
const seedConsulted = (isUpdate, cur, initialPayload) => {
  if (isUpdate && cur.consultedDoctor !== undefined && cur.consultedDoctor !== null) {
    return cur.consultedDoctor ? "yes" : "no";
  }
  if (initialPayload && typeof initialPayload.consulted_doctor === "boolean") {
    return initialPayload.consulted_doctor ? "yes" : "no";
  }
  return isUpdate ? "no" : null;
};

const ComplaintForm = ({
  formId,
  patientId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extensions (all optional; defaults preserve standalone).
  initialPayload,
  onSave,
  onReject,
  isReopen,
  isAiSourced,
  extraActiveKeys,
  saving,
  error,
  themeClass,
  menuClassName,
  lockComplaint,
}) => {
  const isUpdate = !!editing;
  const cur = (editing && editing.current) || {};
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";

  // ZY-10356 Phase 1 — transcript-mode draft preservation. Standalone
  // mounts return [null, no-op] and this entire block is inert. Hydrate
  // each useState below from `draft` when present (prefix every
  // initializer); the useEffect at the bottom of this component writes
  // the full snapshot back on every field change.
  const [draft, saveDraft] = useCardDraft(formId);
  // Complaint name + ICD are chosen once, at first-time add. Every re-open
  // (update, in-call re-edit, review click-to-edit) treats them as
  // read-only — matches the standalone update card, which has never
  // exposed the ICD picker. `isUpdate` implies this automatically; add-mode
  // callers set `lockComplaint` explicitly when reopening a saved item.
  const lockComplaintName = isUpdate || !!lockComplaint;

  // Update mode reads the complaint's name off `editing` and never edits it,
  // so the ICD fields stay blank — only the four the status write touches get
  // seeded, exactly as the dialog seeded them. Transcript-mode add pre-fills
  // from `initialPayload` when provided (falling back to `value` because the
  // agent's Suggestion dataclass stores the primary text there, not on
  // `complaint`).
  const [complaint, setComplaint] = useState(() => {
    if (draft && "complaint" in draft) return draft.complaint;
    if (isUpdate) return "";
    if (p) return p.complaint || p.value || "";
    return "";
  });
  const [complaintCode, setComplaintCode] = useState(() => {
    if (draft && "complaintCode" in draft) return draft.complaintCode;
    if (p && p.icd_code) {
      return { icdCode: p.icd_code, icdDescription: p.icd_description || "" };
    }
    return null;
  });
  // AI-suggested ICD chip payload — frozen at mount from the captured
  // item (transcript-mode only). Passed straight to IcdAutocomplete
  // which owns the pill markup + click behavior; ComplaintForm just
  // carries the shape through. Null on standalone add / update sheets,
  // AND on manual-sourced transcript cards (whether first-open or
  // reedit) — the "✦ AI" badge only reads correctly when the ICD
  // actually came from the AI pipeline (design doc §4.2).
  const aiSuggestion = isAiSourced && p && p.icd_code
    ? { icdCode: p.icd_code, extractedText: p.complaint || p.value || "" }
    : null;
  const [collapsed, setCollapsed] = useState(false);
  const [bandKey, setBandKey] = useState(() => {
    if (draft && "bandKey" in draft) return draft.bandKey;
    if (isUpdate) return bandForScore(cur.vas).key;
    if (p) return bandKeyFromIntensity(p.intensity);
    return initialBand().key;
  });
  const initialDur = durationFromPayloadSince(p && p.since_date);
  const [years, setYears] = useState(() => (draft && "years" in draft ? draft.years : initialDur.years));
  const [months, setMonths] = useState(() => (draft && "months" in draft ? draft.months : initialDur.months));
  const [days, setDays] = useState(() => (draft && "days" in draft ? draft.days : initialDur.days));
  // "From today" toggle — sets since-when to today without asking the
  // reviewer to type 0/0/0. Only interactable while every stepper is
  // empty or zero (`durationAllZero` below); typing any non-zero value
  // auto-clears it. Seeded true when the AI captured a since_date that
  // parses to a zero duration (i.e. today) so the auto-calc line lands
  // on today without the reviewer having to flip the switch manually.
  const [fromToday, setFromToday] = useState(() => {
    if (draft && "fromToday" in draft) return draft.fromToday;
    if (isUpdate || !p || !p.since_date) return false;
    return initialDur.years === 0 && initialDur.months === 0 && initialDur.days === 0;
  });
  // ZY-10312 — the Update card's Status update date defaults to TODAY
  // on every open (mirrors Medicine wave-1 Req 5). The reviewer can
  // still edit the picker to any date between the latest audit entry's
  // sinceWhen and today; seeding the stored sinceWhen made every open
  // land on the previous update's date, which read as "already set,
  // nothing to do" even when the reviewer intended a fresh status row.
  const [statusDate, setStatusDate] = useState(() => (
    draft && "statusDate" in draft ? draft.statusDate : todayISO()
  ));
  // Historical-resolve-on-add: when the reviewer picks `Resolved` on a new
  // complaint, these two inputs surface so the pre-resolution segment and
  // the resolved row land in the audit together (otherwise the complaint
  // would read as "created already resolved" with no prior-severity row).
  // Both fields reset the moment the status leaves "Resolved" — see
  // changeBandKey below. Default previous status mirrors the mockup
  // ("Mild (1-3)"); resolved date is required (no default — product wants
  // the reviewer to think about when it actually resolved).
  // Reedit of a resolve-on-add card: seed from the captured payload's
  // `previous_vas` + `resolved_date` (written by bodyToCapturedAdd on
  // first save) so historicalResolveValid can satisfy on reopen. Fresh
  // AI emits / manual adds have neither key present → defaults
  // preserved. Standalone-add never passes `initialPayload`, unchanged.
  const [previousBandKey, setPreviousBandKey] = useState(() => {
    if (draft && "previousBandKey" in draft) return draft.previousBandKey;
    if (p && typeof p.previous_vas === "number") return bandForScore(p.previous_vas).key;
    return "mild";
  });
  const [resolvedDate, setResolvedDate] = useState(() => {
    if (draft && "resolvedDate" in draft) return draft.resolvedDate;
    if (p && p.resolved_date) return p.resolved_date;
    return "";
  });
  const [description, setDescription] = useState(() => {
    if (draft && "description" in draft) return draft.description;
    if (isUpdate) return cur.description || "";
    if (p) return p.description || "";
    return "";
  });
  // The board marks "Consulted doctor?" REQUIRED (*) — on ADD there is no
  // silent default, the user must pick before Save enables. On UPDATE the
  // record's own value seeds it, so it is never null there.
  const [consulted, setConsulted] = useState(() => (
    draft && "consulted" in draft ? draft.consulted : seedConsulted(isUpdate, cur, p)
  ));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync all persistent field values to the
  // transcript-mode draft store on every change. No-op in standalone
  // (saveDraft is a no-op outside the CardDraftProvider). `submitting`
  // and `feedback` are ephemeral and deliberately excluded.
  useEffect(() => {
    saveDraft({
      complaint,
      complaintCode,
      bandKey,
      years, months, days,
      fromToday,
      statusDate,
      previousBandKey,
      resolvedDate,
      description,
      consulted,
    });
  }, [
    saveDraft,
    complaint, complaintCode, bandKey,
    years, months, days,
    fromToday, statusDate,
    previousBandKey, resolvedDate,
    description, consulted,
  ]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Set of already-recorded active-complaint identifiers (ICD codes first,
  // lowercased name as fallback for free-text-added rows). Fed to the
  // IcdAutocomplete so already-added rows render greyed with an "already
  // added" pill. Only fetched in Add mode — the picker is hidden on the
  // status-update sheet (`lockComplaintName`) so the network call is dead
  // weight there.
  const [activeComplaintKeys, setActiveComplaintKeys] = useState(() => new Set());
  useEffect(() => {
    if (lockComplaintName || !patientId) return;
    const svc = new ChiefComplaintsService();
    svc.list(patientId, (res) => {
      if (!mounted.current) return;
      const keys = new Set();
      for (const c of (res && res.active) || []) {
        const code = ((c && c.complaintCode && c.complaintCode.icdCode) || "")
          .trim()
          .toLowerCase();
        if (code) keys.add(code);
        const nm = ((c && c.complaint) || "").trim().toLowerCase();
        if (nm) keys.add(nm);
      }
      setActiveComplaintKeys(keys);
    }, () => {
      /* No error callback threw "onError1 is not a function" when this read
         failed (bridge's GET retries once, then calls it). A failed read only
         means the already-active warning can't fire; the form still works. */
    });
  }, [patientId, lockComplaintName]);
  const isComplaintAlreadyActive = useCallback(
    (row) => {
      const code = ((row && (row.icdCode || row.code)) || "").trim().toLowerCase();
      if (code && activeComplaintKeys.has(code)) return true;
      // ZY-10312 — transcript mode passes in-call captures via
      // extraActiveKeys so the dropdown flags items staged *this call*
      // the same way DB-persisted actives are flagged. Keys are already
      // lowercased by CapturedSections.
      if (code && extraActiveKeys && extraActiveKeys.has(code)) return true;
      const label = ((row && (row.label || row.value)) || "").trim().toLowerCase();
      // Trim the leading ICD-code prefix so the name check matches the
      // resolver in IcdAutocomplete's render.
      const stripped = code && label.startsWith(code)
        ? label.slice(code.length).replace(/^[\s.:\-–—]+/, "")
        : label;
      if (!stripped) return false;
      return activeComplaintKeys.has(stripped) || (extraActiveKeys && extraActiveKeys.has(stripped));
    },
    [activeComplaintKeys, extraActiveKeys],
  );

  // The rail needs to know whether this sheet has a complaint on it yet: a
  // second "+ Add complaints" click must not bury an untouched blank sheet
  // under a new one. Update sheets always count as filled — their complaint
  // is fixed and comes from the record. Only invoked when the caller wired
  // a `onFilledChange` prop (standalone SheetStack does; transcript doesn't).
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, isUpdate || !!complaint.trim());
    }
  }, [formId, isUpdate, complaint, onFilledChange]);

  // When the reviewer manually lands the trio on 0/0/0 (typing zeros, or
  // arrowing every field down), auto-flip "From today" on. All-zero is
  // semantically "today" — the toggle IS the way we record that intent,
  // so leaving it off would let a same-day entry save with a null derived
  // date. Only fires when the values are explicit zeros; leaves nulls
  // ("-" state) alone so the reviewer can still opt out.
  useEffect(() => {
    if (years === 0 && months === 0 && days === 0 && !fromToday) {
      setFromToday(true);
    }
  }, [years, months, days, fromToday]);
  const durationHasAny = years > 0 || months > 0 || days > 0;
  const derivedSinceWhen = fromToday
    ? new Date()
    : durationHasAny
    ? durationToSinceWhen(years, months, days)
    : null;
  const band = VAS_BANDS.find((b) => b.key === bandKey) || initialBand();
  const previousBand = VAS_BANDS.find((b) => b.key === previousBandKey) || VAS_BANDS[1];
  // Historical-resolve ONLY applies to the Add path (CC v2's UpdateStatus
  // endpoint has no equivalent — resolving an existing complaint uses the
  // normal single-entry status write).
  const needsHistoricalResolve = !isUpdate && bandKey === "absent";
  const resolvedDateMinISO = derivedSinceWhen ? dateInputFrom(derivedSinceWhen) : "";
  const historicalResolveValid = !needsHistoricalResolve || (
    !!previousBandKey
    && !!resolvedDate
    && (!resolvedDateMinISO || resolvedDate >= resolvedDateMinISO)
    && resolvedDate <= todayISO()
  );
  // Reset the historical-resolve inputs the moment the status leaves
  // `absent` — stale values would otherwise ride into a subsequent save if
  // the reviewer flipped back and the fields had already been filled.
  const changeBandKey = (next) => {
    setBandKey(next);
    if (next !== "absent") {
      setResolvedDate("");
    }
  };
  // Save disabled while either the internal write-path is in flight OR the
  // transcript caller is mid-persist. `blockedBySibling` is standalone-only.
  const isBusy = submitting || !!saving;
  // Add-mode requires a REAL ICD binding (product decision): free-text
  // complaints are no longer acceptable via either the standalone
  // dropdown or the transcript review flow. The IcdAutocomplete's
  // onChange contract nulls complaintCode the moment the reviewer
  // types over the text, so this gate reliably re-blocks Save until
  // they pick again from the dropdown. Update mode is unaffected —
  // complaint identity is locked during Status writes.
  const canSubmit =
    !blockedBySibling &&
    (isUpdate
      ? !!statusDate && !isBusy
      : !!complaint.trim() &&
        !!(complaintCode && complaintCode.icdCode) &&
        !isBusy &&
        consulted !== null &&
        !!bandKey &&
        (fromToday || durationHasAny) &&
        historicalResolveValid);

  // Stepper setters that also drop the "From today" flag the moment a
  // non-zero value lands — the toggle is a shortcut for "no elapsed time",
  // so any real duration cancels it. Wrapping every setter keeps the
  // invariant local instead of re-checking it on submit.
  const changeYears = (v) => {
    setYears(v);
    if (v > 0) setFromToday(false);
  };
  const changeMonths = (v) => {
    setMonths(v);
    if (v > 0) setFromToday(false);
  };
  const changeDays = (v) => {
    setDays(v);
    if (v > 0) setFromToday(false);
  };
  const toggleFromToday = () => {
    if (fromToday) {
      // Turning off resets the steppers to empty ("-"). Users flip this
      // to abandon "today" and start entering a real duration instead;
      // leaving stale zeros would still read as "since today" via the
      // auto-calc line.
      setFromToday(false);
      setYears(null);
      setMonths(null);
      setDays(null);
      return;
    }
    // Turning on always resets the stack to 0/0/0 — the toggle IS "since
    // today", so any manually-typed duration is intentionally overridden.
    // Toggle stays interactable regardless of current field values (user
    // request: never grey out).
    setFromToday(true);
    setYears(0);
    setMonths(0);
    setDays(0);
  };

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;

    // Update writes the same four fields the dialog did; add additionally
    // carries the complaint itself and its ICD code. Shape is CC v2's
    // request body (camelCase) — the transcript caller adapts to the
    // captured-item shape (snake_case) at the callsite.
    const body = isUpdate
      ? {
          vas: band.min,
          sinceWhen: toIsoUtc(statusDate),
          description: description.trim(),
          consultedDoctor: consulted === "yes",
        }
      : {
          complaint: complaint.trim(),
          complaintCode: complaintCode || undefined,
          vas: band.min,
          // Duration / From-today never collect a time of day — strip the
          // wall-clock time the derivation picked up from `new Date()` so
          // the wire value matches UpdateStatus's date-picker sinceWhen
          // (UTC midnight of the calendar day). When future UX adds an
          // explicit datetime picker, swap this back to `toIsoUtc` so the
          // user-input time flows through.
          sinceWhen: toIsoDateOnly(derivedSinceWhen),
          description: description.trim(),
          consultedDoctor: consulted === "yes",
          // Historical-resolve: fields only land on the wire when the
          // reviewer picked `Resolved` AND filled both inputs. canSubmit
          // already enforces this, so the guard here is just belt-and-
          // braces so a stale non-"absent" bandKey doesn't send stale
          // previous-status values.
          ...(needsHistoricalResolve && resolvedDate ? {
            previousVas: previousBand.min,
            resolvedDate: toIsoUtc(resolvedDate),
          } : {}),
        };

    // Transcript mode — hand the body out; caller owns the persistence.
    // No local `submitting` flag here (the caller drives the `saving`
    // prop) and no CC_V2_REFRESH emit (transcript polls its own state).
    if (isTranscript) {
      onSave(body);
      return;
    }

    // Standalone mode — direct CC v2 write with optimistic-lock version.
    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    setFeedback(null);
    const svc = new ChiefComplaintsService();
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet, and closeForm
      // clears the saving flag on the way out — so there is nothing to hand
      // back and no parent state to touch.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({
        kind: "err",
        text:
          (err && err.message) ||
          (isUpdate ? "Update failed — please retry" : "Save failed — please retry"),
      });
    };
    const done = () => {
      EventService.emit(CC_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    // Fresh version per mutation, never cached. Sibling sheets are blocked
    // from saving while this is in flight, so the version read here cannot go
    // stale between the read and the write.
    svc.list(
      patientId,
      (fresh) => {
        const withVersion = { ...body, expectedVersion: (fresh && fresh.version) || 0 };
        if (isUpdate) {
          svc.updateStatus(patientId, editing.complaintId, withVersion, done, failed);
        } else {
          svc.add(patientId, withVersion, done, failed);
        }
      },
      failed
    );
  };

  // Unique per sheet — stacked cards would otherwise share DOM ids and every
  // label would point at the first card's field.
  const descId = `cc3-desc-${formId}`;
  const dateId = `cc3-status-date-${formId}`;
  const resolvedId = `cc3-resolved-date-${formId}`;
  const foldId = `cc3-fold-${formId}`;


  const rootClass = [
    "cc3-form",
    isUpdate ? "cc3-update" : "",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");

  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";
  const displayErrorKind = feedback && feedback.kind === "err" ? "err" : (error ? "err" : "");

  return (
    <div className={rootClass}>
      {/* ZY-10356 Phase 6 — top-right X close (design doc §3 "X close
          icon appears if and only if the card is a re-open of something
          committed earlier in this call"). First-opens (#1, #3, #5, #7)
          do NOT show the X — the footer Reject is the only dismiss
          affordance. Re-opens (#2, #4, #6, #8) show the X and it fires
          revertCaptured via the parent's onClose (SAFE semantics vs
          Reject's DESTRUCTIVE). */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={() => onClose(formId)}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* The chevron-up is this design system's card-collapse affordance
          (cf. ad3-dlg-chev / pa3-dlg-chev) — it folds everything below this
          row, footer included. Reject (footer) is the audit decision. */}
      {isUpdate ? (
        <div className="cc3-lbl-row cc3-update-head">
          <span className="cc3-update-title">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Update <span className="cc3-update-name">&middot; {editing.complaint}</span>
          </span>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this update" : "Collapse this update"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      ) : lockComplaintName ? (
        // Re-open of an already-added item (in-call re-edit, review
        // click-to-edit). Complaint + ICD chose on the first Save; the
        // reviewer can only tweak status / duration / description /
        // consulted from here. Same visual pattern as the update label
        // row so both read-only heads look consistent.
        <div className="cc3-lbl-row cc3-update-head">
          <span className="cc3-update-title">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Complaint <span className="cc3-update-name">&middot; {complaint || "(none)"}</span>
          </span>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this complaint" : "Collapse this complaint"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      ) : (
        <div className="cc3-lbl-row">
          <label className="cc3-lbl">
            {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
            Complaint
            {!lockComplaintName && <span className="cc3-req">*</span>}{" "}
            <span className="cc3-lbl-hint">&middot; ICD-enabled &middot; fuzzy search</span>
          </label>
          <button
            type="button"
            className="cc3-lbl-chev"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-controls={foldId}
            aria-label={collapsed ? "Expand this complaint" : "Collapse this complaint"}
          >
            <ChevronUpIcon />
          </button>
        </div>
      )}

      <div className={`cc3-fold${isUpdate ? "" : " is-tight"}`} id={foldId}>
        <div className="cc3-fold-inner">
      {!lockComplaintName && (
        <div className="cc3-field">
          <IcdAutocomplete
            value={complaint}
            code={complaintCode && complaintCode.icdCode}
            onChange={({ complaint: c, complaintCode: cc }) => {
              setComplaint(c);
              setComplaintCode(cc);
            }}
            disabled={isBusy}
            isAlreadyActive={isComplaintAlreadyActive}
            alreadyActiveTitle="Already in the chief complaints list — update it from the row menu"
            aiSuggestion={aiSuggestion}
          />
        </div>
      )}

      {/* Status · VAS (177) beside the required Consulted doctor? (2964:540) */}
      <div className="cc3-two">
        <div className="cc3-field">
          <label className="cc3-lbl">
            Status<span className="cc3-req">*</span>{" "}
            <span className="cc3-lbl-hint">&middot; VAS scale</span>
          </label>
          <div className="cc3-vas" style={{ "--cc3-dot": band.dot || band.fg }}>
            <Select
              value={bandKey}
              options={VAS_BANDS}
              getKey={(b) => b.key}
              getLabel={(b) => b.label}
              onChange={changeBandKey}
              /* When rendered inside the transcript modal, the caller passes
                 `menuClassName="ait-menu"` so the portaled menu (default
                 z-index 200) can be raised above the modal overlay
                 (z-index 1500) via a scoped `body >` selector. Standalone
                 leaves it empty — Select ignores empty strings. */
              menuClassName={menuClassName || ""}
            />
          </div>
        </div>
        <div className="cc3-field">
          <label className="cc3-lbl">
            Consulted doctor?{" "}<span className="cc3-req">*</span>
          </label>
          <YesNo
            value={consulted}
            onChange={setConsulted}
            /* This prop IS the radiogroup's accessible name (YesNo renders
               buttons, not inputs — there is no name= attribute to namespace).
               The sheet's own role="group" label disambiguates the stack. */
            name="Consulted doctor?"
          />
        </div>
      </div>

      {/* Add derives sinceWhen from a duration; update edits the date the
          record already carries (the dialog's "Status update date" box). */}
      {isUpdate ? (
        <div className="cc3-field">
          <label className="cc3-lbl" htmlFor={dateId}>
            Status update date<span className="cc3-req">*</span>
          </label>
          <div className="an3-date-wrap">
            <input
              id={dateId}
              className="sec-input"
              type="date"
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
              // Bounded on the lower end by the LATEST `sinceWhen` on the
              // audit (not the episode start): a new status row must be
              // dated on or after the most recent entry already logged,
              // so the clinician can't record a change that predates one
              // already saved. Upper bound stays at today — no future
              // dates.
              min={dateInputFrom(latestSinceWhen(
                editing && editing.statusHistory,
                cur.sinceWhen || (editing && editing.addedAt),
              ))}
              max={todayISO()}
              disabled={isBusy}
            />
            <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
          </div>
        </div>
      ) : (
        <div className="cc3-field">
          {/* Label sits on the left, "From today" toggle on the right —
              same row so the toggle explicitly labels the shortcut it
              provides (set since-when to today without touching Y/M/D).
              The checkbox is disabled whenever ANY stepper carries a
              non-zero number: a real duration and "today" are mutually
              exclusive, so the affordance only makes sense while the
              stack is empty/zero. */}
          <div className="cc3-duration-head">
            <label className="cc3-lbl">
              Duration<span className="cc3-req">*</span>
            </label>
            <label className="med3-toggle-wrap cc3-from-today">
              <input
                type="checkbox"
                checked={fromToday}
                onChange={toggleFromToday}
                disabled={isBusy}
              />
              <span className="med3-toggle" aria-hidden="true" />
              <span className="med3-toggle-lbl">From today</span>
            </label>
          </div>
          <div className="cc-duration" role="group" aria-label="Duration">
            <Stepper label="Years" value={years} onChange={changeYears} max={120} nullable disabled={isBusy} />
            <Stepper label="Months" value={months} onChange={changeMonths} max={11} nullable disabled={isBusy} />
            <Stepper label="Days" value={days} onChange={changeDays} max={31} nullable disabled={isBusy} />
          </div>
          <p className="cc3-autocalc">
            Auto-calc &middot; Since when: <strong>{formatShortDate(derivedSinceWhen)}</strong>
          </p>
        </div>
      )}

      {/* Historical-resolve-on-add: when the reviewer picks `Resolved` on a
          new complaint we need two more inputs so the audit carries both
          the pre-resolution segment and the resolve itself in one write.
          `Previous status` filters out `absent` — the pre-resolution state
          can't itself be Resolved. `Resolved date` is bounded by the
          derived since-when at the low end and today at the high end
          (same-day resolution is allowed). Hidden on the Update sheet
          (that path uses the single-entry status write). */}
      {needsHistoricalResolve && (
        <div className="cc3-two">
          <div className="cc3-field">
            <label className="cc3-lbl" htmlFor={resolvedId}>
              Resolved date<span className="cc3-req">*</span>
            </label>
            <div className="an3-date-wrap">
              <input
                id={resolvedId}
                className="sec-input"
                type="date"
                value={resolvedDate}
                onChange={(e) => setResolvedDate(e.target.value)}
                min={resolvedDateMinISO || undefined}
                max={todayISO()}
                // Locked until Duration gives us an onset date — otherwise
                // the reviewer could pick an unbounded resolved date and
                // the min= constraint would be meaningless.
                disabled={isBusy || !derivedSinceWhen}
              />
              <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </div>
          </div>
          <div className="cc3-field">
            <label className="cc3-lbl">
              Previous status<span className="cc3-req">*</span>
            </label>
            <div className="cc3-vas" style={{ "--cc3-dot": previousBand.dot || previousBand.fg }}>
              <Select
                value={previousBandKey}
                options={VAS_BANDS.filter((b) => b.key !== "absent")}
                getKey={(b) => b.key}
                getLabel={(b) => b.label}
                onChange={setPreviousBandKey}
                menuClassName={menuClassName || ""}
              />
            </div>
          </div>
        </div>
      )}

      <div className="cc3-field">
        <div className="cc3-lbl-row">
          <label className="cc3-lbl" htmlFor={descId}>
            Description <span className="cc3-lbl-opt">optional</span>
          </label>
          <span className="cc3-lbl-max">
            {description
              ? `${countWords(description)} / ${MAX_DESC_WORDS} words`
              : `Max ${MAX_DESC_WORDS} words`}
          </span>
        </div>
        <textarea
          id={descId}
          className="cc3-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(capWords(e.target.value))}
          placeholder={isUpdate ? "Add a short note about this update…" : undefined}
          disabled={isBusy}
        />
      </div>

      {displayError && <div className={`cc-feedback is-${displayErrorKind}`}>{displayError}</div>}

      <div className="cc3-foot">
        {/* Stays live while saving ON PURPOSE. A write that never settles
            (the token-refresh path can swallow its callbacks) would otherwise
            pin the stack-wide save lock with no way out — every sheet's Save
            dead and this sheet undismissable until a reload. Closing mid-write
            is safe: the rail clears the lock, and both done() and failed()
            bail on !mounted, so a late response touches nothing. */}
        <button
          type="button"
          className="cc3-cancel"
          onClick={() => {
            // ZY-10356 Phase 6 — transcript-mode Reject (design doc §3.1):
            //   • #1/#3 blank form → fall through to local-drop onClose.
            //   • #1/#3 with content → propose+reject so the server
            //     records what the reviewer typed before dismissing.
            //   • #6/#8 (isUpdate — identity lives in `editing`, not the
            //     form's complaint state which is intentionally empty) →
            //     always onReject so the server soft-deletes the staged
            //     update. Previously the empty `complaint.trim()` gate
            //     dropped these onto onClose → revertCaptured instead of
            //     rejectCaptured, so the row kept its "Update" tag.
            //   • #2/#4 re-opens have the complaint pre-populated from
            //     captured.current, so complaint.trim() picks them up.
            if (isTranscript && typeof onReject === "function" && (isUpdate || complaint.trim())) {
              const partialBody = isUpdate
                ? null
                : {
                  complaint: complaint.trim(),
                  complaintCode: complaintCode || undefined,
                  description: description.trim(),
                };
              onReject(formId, partialBody);
              return;
            }
            onClose(formId);
          }}
        >
          {isBusy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
        </button>
        <button
          type="button"
          className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSubmit}
          title={blockedBySibling ? "Another complaint is saving" : undefined}
        >
          {isBusy
            ? (isUpdate ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
            : blockedBySibling
            ? "Waiting…"
            : isUpdate
            ? "Update"
            : isTranscript
            ? "Add"
            : "Save complaint"}
        </button>
      </div>
        </div>
      </div>
    </div>
  );
};

ComplaintForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Standalone (CC v2 write path) only — the target patient. */
  patientId: PropTypes.number,
  /** null = blank add; a complaint record = updating that record's status. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  /** ZY-10356 Phase 6 — transcript-mode Reject handler. Receives
   *  (formId, partialBody) when the reviewer clicks Reject with the
   *  form at least partially filled (complaint name non-empty).
   *  Parent is expected to propose+reject so the audit row shows with
   *  strikethrough. Unset falls back to onClose (local-drop). */
  onReject: PropTypes.func,
  /** True when this card is a re-open of a previously-committed item
   *  (design doc §3 cases #2/#4/#6/#8). Gates the top-right X close
   *  icon — first-opens show no X. */
  isReopen: PropTypes.bool,
  /** True when the captured item came from the AI pipeline (vs reviewer
   *  manual entry). Gates the "✦ AI <ICD>" badge above the search box —
   *  showing it on a manual-sourced reedit would mis-attribute the ICD
   *  the reviewer picked themselves. Default false. */
  isAiSourced: PropTypes.bool,
  /** ZY-10312 — transcript-mode set of lowercased ICD codes (and/or
   *  complaint names) that have already been staged THIS call. Merged
   *  into the "already added" predicate so the search dropdown flags
   *  in-call duplicates the same way DB-persisted actives are flagged.
   *  Standalone callers leave this null. */
  extraActiveKeys: PropTypes.instanceOf(Set),
  // Transcript-mode extensions — all optional; unset preserves standalone behaviour.
  /**
   * AI-captured payload used to pre-fill the form (add mode only). Same
   * shape the agent emits: {complaint, icd_code, icd_description,
   * intensity, since_date, description, consulted_doctor}. Fields missing
   * from the payload fall through to the standalone defaults.
   */
  initialPayload: PropTypes.object,
  /**
   * When provided, the form skips its own CC v2 write path and hands the
   * CC v2 request body to this callback. Caller owns persistence — used
   * by the transcript flow to stash captured items on the transcript
   * instead of writing directly to CC v2.
   */
  onSave: PropTypes.func,
  /** External save-in-flight indicator; disables the buttons when true. */
  saving: PropTypes.bool,
  /** External error message from the caller (transcript mode). */
  error: PropTypes.string,
  /** "is-ai" | "is-manual" | "is-update" — extra class on the root + Save button. */
  themeClass: PropTypes.string,
  /** When true, renders the transcript "AI captured" / "Updating" chip above the label row. */
  /** Extra class on the VAS Select's portaled menu — used for z-index scoping inside the modal. */
  menuClassName: PropTypes.string,
  /**
   * Lock the complaint (name + ICD) as read-only. Automatically implied
   * by update mode; add-mode callers set this true when reopening an
   * already-saved captured item (in-call re-edit, review click-to-edit)
   * so the reviewer can only tweak status / duration / description /
   * consulted.
   */
  lockComplaint: PropTypes.bool,
};

ComplaintForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  onReject: null,
  isReopen: false,
  isAiSourced: false,
  extraActiveKeys: null,
  saving: false,
  error: "",
  themeClass: "",
  menuClassName: "",
  lockComplaint: false,
};

export default ComplaintForm;
